import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Option,
  Select,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from '@mui/joy';
import type { DooColour, DooGlass, DooHardware, DooHardwareType } from '../types';
import {
  listColours,
  listDoorStyles,
  listGlass,
  listHardware,
  listHardwareCollections,
  listHardwareFinishes,
  listHardwareTypes,
  listMaterials,
  listAllStyleMappings,
  listSuppliers,
} from '../data/repository';
import {
  HARDWARE_FINISH_JUNCTION,
  MAPPING_COLOUR_JUNCTION,
  MAPPING_GLASS_JUNCTION,
  MAPPING_HARDWARE_JUNCTION,
  listAllJunctionRows,
  loadMappingCompatibility,
  saveMappingCompatibility,
  setJunctionPair,
} from '../data/compatibility';
import { useAsyncList } from './NamedEntitySection';
import { ActionRow, OracleTable, SectionCard } from './FormControls';

export function CompatibilitySection({
  initialDoorStyleId,
}: {
  initialDoorStyleId?: string | null;
}) {
  return (
    <Tabs defaultValue="door">
      <TabList>
        <Tab value="door">Door-centric</Tab>
        <Tab value="matrix">Matrix</Tab>
      </TabList>
      <TabPanel value="door" sx={{ px: 0 }}>
        <DoorCentricEditor initialDoorStyleId={initialDoorStyleId} />
      </TabPanel>
      <TabPanel value="matrix" sx={{ px: 0 }}>
        <CompatibilityMatrix />
      </TabPanel>
    </Tabs>
  );
}

function DoorCentricEditor({ initialDoorStyleId }: { initialDoorStyleId?: string | null }) {
  const styles = useAsyncList(listDoorStyles);
  const mappings = useAsyncList(listAllStyleMappings);
  const suppliers = useAsyncList(listSuppliers);
  const materials = useAsyncList(listMaterials);
  const colours = useAsyncList(listColours);
  const glass = useAsyncList(listGlass);
  const hardware = useAsyncList(listHardware);
  const finishes = useAsyncList(listHardwareFinishes);
  const types = useAsyncList(listHardwareTypes);
  const collections = useAsyncList(listHardwareCollections);
  const [hardwareFinishIdsByProduct, setHardwareFinishIdsByProduct] = useState<Map<string, string[]>>(
    new Map()
  );
  const [doorStyleId, setDoorStyleId] = useState<string | null>(initialDoorStyleId ?? null);
  const [mappingId, setMappingId] = useState<string | null>(null);
  const [colourIds, setColourIds] = useState<string[]>([]);
  const [glassIds, setGlassIds] = useState<string[]>([]);
  const [hardwareIds, setHardwareIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialDoorStyleId) setDoorStyleId(initialDoorStyleId);
  }, [initialDoorStyleId]);

  const styleMappings = useMemo(
    () => mappings.rows.filter((row) => row.door_style_id === doorStyleId),
    [mappings.rows, doorStyleId]
  );

  useEffect(() => {
    if (!doorStyleId) {
      setMappingId(null);
      return;
    }
    if (!styleMappings.some((row) => row.mapping_id === mappingId)) {
      setMappingId(styleMappings[0]?.mapping_id ?? null);
    }
  }, [doorStyleId, styleMappings, mappingId]);

  useEffect(() => {
    if (!mappingId) {
      setColourIds([]);
      setGlassIds([]);
      setHardwareIds([]);
      return;
    }
    setLoading(true);
    loadMappingCompatibility(mappingId)
      .then((next) => {
        setColourIds(next.colourIds);
        setGlassIds(next.glassIds);
        setHardwareIds(next.hardwareIds);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load compatibility'))
      .finally(() => setLoading(false));
  }, [mappingId]);

  useEffect(() => {
    listAllJunctionRows(HARDWARE_FINISH_JUNCTION)
      .then((rows) => {
        const map = new Map<string, string[]>();
        rows.forEach((row) => {
          const hardwareId = row.hardware_id;
          const finishId = row.finish_id;
          if (!hardwareId || !finishId) return;
          const current = map.get(hardwareId) ?? [];
          current.push(finishId);
          map.set(hardwareId, current);
        });
        setHardwareFinishIdsByProduct(map);
      })
      .catch(() => undefined);
  }, []);

  const finishName = (finishId: string) =>
    finishes.rows.find((row) => row.finish_id === finishId)?.finish_name ?? 'Unknown';

  const groupedHardware = useMemo(
    () =>
      groupHardware(
        hardware.rows,
        types.rows,
        collections.rows,
        hardwareFinishIdsByProduct,
        finishName
      ),
    [hardware.rows, types.rows, collections.rows, hardwareFinishIdsByProduct, finishes.rows]
  );

  const selectedStyle = styles.rows.find((row) => row.door_style_id === doorStyleId);
  const selectedMapping = styleMappings.find((row) => row.mapping_id === mappingId);
  const materialName =
    materials.rows.find((row) => row.material_id === selectedStyle?.material_id)?.material_name ?? null;
  const supplierName =
    suppliers.rows.find((row) => row.supplier_id === selectedMapping?.supplier_id)?.supplier_name ?? null;

  return (
    <Box sx={{ maxWidth: 960 }}>
      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}
      {message ? <Alert color="success" sx={{ mb: 2 }}>{message}</Alert> : null}

      <SectionCard
        title="Door style & supplier"
        subtitle="Compatibility is stored per supplier mapping (door style + supplier)."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 480 }}>
          <Select
            placeholder="Choose door style"
            value={doorStyleId}
            onChange={(_, value) => setDoorStyleId((value as string) || null)}
          >
            {styles.rows.map((row) => (
              <Option key={row.door_style_id} value={row.door_style_id}>
                {row.keystone_style_name || row.door_style_id}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Choose supplier mapping"
            value={mappingId}
            disabled={!doorStyleId || styleMappings.length === 0}
            onChange={(_, value) => setMappingId((value as string) || null)}
          >
            {styleMappings.map((row) => {
              const supplier =
                suppliers.rows.find((item) => item.supplier_id === row.supplier_id)?.supplier_name ??
                row.supplier_id;
              return (
                <Option key={row.mapping_id} value={row.mapping_id}>
                  {supplier} — {row.supplier_style_name || 'Unnamed'}
                </Option>
              );
            })}
          </Select>
        </Box>

        {selectedStyle ? (
          <ActionRow sx={{ mt: 1.5 }}>
            {selectedStyle.collection ? (
              <Chip size="sm" variant="soft">{selectedStyle.collection}</Chip>
            ) : null}
            {materialName ? <Chip size="sm" variant="outlined">{materialName}</Chip> : null}
            {supplierName ? <Chip size="sm" variant="outlined">{supplierName}</Chip> : null}
            {selectedMapping?.supplier_style_name ? (
              <Chip size="sm" variant="outlined">{selectedMapping.supplier_style_name}</Chip>
            ) : null}
          </ActionRow>
        ) : null}
      </SectionCard>

      {!doorStyleId ? (
        <Alert color="neutral">Select a door style to edit its compatibility.</Alert>
      ) : styleMappings.length === 0 ? (
        <Alert color="warning" sx={{ mt: 2 }}>
          Add a supplier mapping on the door style before setting compatibility.
        </Alert>
      ) : !mappingId ? (
        <Alert color="neutral">Select a supplier mapping to edit compatibility.</Alert>
      ) : loading ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          Loading compatibility…
        </Typography>
      ) : (
        <SectionCard
          title={
            selectedMapping
              ? `${selectedStyle?.keystone_style_name || 'Door'} · ${supplierName || 'Supplier'}`
              : 'Compatibility'
          }
          subtitle="Tick the catalogue items allowed for this door style and supplier combination."
          action={
            <Button
              size="sm"
              loading={saving}
              onClick={async () => {
                if (!mappingId) return;
                setSaving(true);
                setMessage(null);
                try {
                  await saveMappingCompatibility(mappingId, {
                    colourIds,
                    glassIds,
                    hardwareIds,
                  });
                  setMessage('Compatibility saved.');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Save failed');
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save compatibility
            </Button>
          }
        >
          <Tabs defaultValue="colours">
            <TabList sx={{ mb: 1.5 }}>
              <Tab value="colours">Colours ({colourIds.length})</Tab>
              <Tab value="glass">Glass ({glassIds.length})</Tab>
              <Tab value="hardware">Hardware ({hardwareIds.length})</Tab>
            </TabList>
            <TabPanel value="colours" sx={{ px: 0, pt: 0 }}>
              <CheckboxGroup
                items={colours.rows.map((row) => ({ id: row.colour_id, label: row.colour_name }))}
                selected={colourIds}
                onChange={setColourIds}
                emptyMessage="No colours in the catalogue yet."
              />
            </TabPanel>
            <TabPanel value="glass" sx={{ px: 0, pt: 0 }}>
              <CheckboxGroup
                items={glass.rows.map((row) => ({ id: row.glass_id, label: row.glass_name }))}
                selected={glassIds}
                onChange={setGlassIds}
                emptyMessage="No glass options in the catalogue yet."
              />
            </TabPanel>
            <TabPanel value="hardware" sx={{ px: 0, pt: 0 }}>
              <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 1.5 }}>
                Enable hardware products for this mapping. Available metal finishes are defined on each
                hardware product and flow through automatically when customers configure a door.
              </Typography>
              {groupedHardware.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  No hardware in the catalogue yet.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {hardwareIds.length}/
                      {groupedHardware.reduce((count, group) => count + group.items.length, 0)} selected
                    </Typography>
                    <ActionRow>
                      <Button
                        size="sm"
                        variant="plain"
                        onClick={() =>
                          setHardwareIds(
                            groupedHardware.flatMap((group) => group.items.map((item) => item.id))
                          )
                        }
                      >
                        Select all
                      </Button>
                      <Button size="sm" variant="plain" onClick={() => setHardwareIds([])}>
                        Deselect all
                      </Button>
                    </ActionRow>
                  </Box>
                  {groupedHardware.map((group) => (
                    <CheckboxGroup
                      key={group.title}
                      title={group.title}
                      items={group.items}
                      selected={hardwareIds}
                      onChange={setHardwareIds}
                    />
                  ))}
                </>
              )}
            </TabPanel>
          </Tabs>
        </SectionCard>
      )}
    </Box>
  );
}

function CheckboxGroup({
  title,
  items,
  selected,
  onChange,
  emptyMessage,
}: {
  title?: string;
  items: Array<{ id: string; label: string }>;
  selected: string[];
  onChange: (ids: string[]) => void;
  emptyMessage?: string;
}) {
  if (items.length === 0) {
    return emptyMessage ? (
      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
        {emptyMessage}
      </Typography>
    ) : null;
  }

  const itemIds = items.map((item) => item.id);
  const selectedInGroup = items.filter((item) => selected.includes(item.id)).length;
  const allSelected = selectedInGroup === items.length;

  const selectAll = () => {
    onChange([...new Set([...selected, ...itemIds])]);
  };

  const deselectAll = () => {
    const groupIds = new Set(itemIds);
    onChange(selected.filter((id) => !groupIds.has(id)));
  };

  return (
    <Box sx={{ mb: title ? 2 : 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        {title ? (
          <Typography level="title-sm">
            {title}
            <Typography component="span" level="body-xs" sx={{ color: 'neutral.500', ml: 1 }}>
              {selectedInGroup}/{items.length} selected
            </Typography>
          </Typography>
        ) : (
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            {selectedInGroup}/{items.length} selected
          </Typography>
        )}
        <ActionRow>
          <Button size="sm" variant="plain" disabled={allSelected} onClick={selectAll}>
            Select all
          </Button>
          <Button size="sm" variant="plain" disabled={selectedInGroup === 0} onClick={deselectAll}>
            Deselect all
          </Button>
        </ActionRow>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 0.75,
          p: 1.5,
          borderRadius: 'sm',
          bgcolor: 'background.level1',
        }}
      >
        {items.map((item) => (
          <Checkbox
            key={item.id}
            size="sm"
            label={item.label}
            checked={selected.includes(item.id)}
            onChange={(event) => {
              const checked = event.target.checked;
              onChange(
                checked ? [...selected, item.id] : selected.filter((id) => id !== item.id)
              );
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function CompatibilityMatrix() {
  const styles = useAsyncList(listDoorStyles);
  const mappings = useAsyncList(listAllStyleMappings);
  const suppliers = useAsyncList(listSuppliers);
  const hardware = useAsyncList(listHardware);
  const colours = useAsyncList(listColours);
  const glass = useAsyncList(listGlass);
  const [kind, setKind] = useState<'hardware' | 'colours' | 'glass'>('hardware');
  const [pairs, setPairs] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const spec =
    kind === 'hardware'
      ? MAPPING_HARDWARE_JUNCTION
      : kind === 'colours'
        ? MAPPING_COLOUR_JUNCTION
        : MAPPING_GLASS_JUNCTION;

  const rows =
    kind === 'hardware' ? hardware.rows : kind === 'colours' ? colours.rows : glass.rows;

  const mappingColumns = useMemo(
    () =>
      mappings.rows.map((mapping) => {
        const style = styles.rows.find((row) => row.door_style_id === mapping.door_style_id);
        const supplier = suppliers.rows.find((row) => row.supplier_id === mapping.supplier_id);
        const styleLabel = style?.keystone_style_name || mapping.door_style_id;
        const supplierLabel = supplier?.supplier_name || mapping.supplier_id;
        const shortStyle = abbreviateStyleName(styleLabel);
        const shortSupplier = abbreviateStyleName(supplierLabel);
        return {
          mappingId: mapping.mapping_id,
          label: `${shortStyle} · ${shortSupplier}`,
          title: `${styleLabel} / ${supplierLabel}${
            mapping.supplier_style_name ? ` (${mapping.supplier_style_name})` : ''
          }`,
        };
      }),
    [mappings.rows, styles.rows, suppliers.rows]
  );

  const reloadPairs = () => {
    listAllJunctionRows(spec)
      .then((data) => {
        setPairs(
          new Set(
            data.map((row) => `${row[spec.parentColumn]}:${row[spec.childColumn]}`)
          )
        );
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load matrix'));
  };

  useEffect(() => {
    reloadPairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const rowId = (row: DooHardware | DooColour | DooGlass) => {
    if ('hardware_id' in row) return row.hardware_id;
    if ('colour_id' in row) return row.colour_id;
    return row.glass_id;
  };
  const rowLabel = (row: DooHardware | DooColour | DooGlass) => {
    if ('hardware_name' in row) return row.hardware_name;
    if ('colour_name' in row) return row.colour_name;
    return row.glass_name;
  };

  const kindLabel = kind === 'hardware' ? 'Hardware' : kind === 'colours' ? 'Colour' : 'Glass';

  return (
    <Box>
      {error ? <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert> : null}
      <SectionCard
        title="Compatibility matrix"
        subtitle="Bulk edit pairings across all supplier mappings (door style + supplier)."
      >
        <Select
          value={kind}
          onChange={(_, value) => setKind((value as typeof kind) || 'hardware')}
          sx={{ mb: 2, maxWidth: 240 }}
        >
          <Option value="hardware">Hardware</Option>
          <Option value="colours">Colours</Option>
          <Option value="glass">Glass</Option>
        </Select>
        <OracleTable>
          <thead>
            <tr>
              <th>{kindLabel}</th>
              {mappingColumns.map((column) => (
                <th key={column.mappingId} title={column.title}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={mappingColumns.length + 1}>No {kindLabel.toLowerCase()} items yet.</td>
              </tr>
            ) : mappingColumns.length === 0 ? (
              <tr>
                <td colSpan={2}>No supplier mappings yet.</td>
              </tr>
            ) : (
              rows.map((row) => {
                const childId = rowId(row);
                return (
                  <tr key={childId}>
                    <td>{rowLabel(row)}</td>
                    {mappingColumns.map((column) => {
                      const key = `${column.mappingId}:${childId}`;
                      const allowed = pairs.has(key);
                      return (
                        <td key={column.mappingId}>
                          <Checkbox
                            checked={allowed}
                            overlay
                            onChange={async (event) => {
                              const next = event.target.checked;
                              try {
                                await setJunctionPair(spec, column.mappingId, childId, next);
                                setPairs((current) => {
                                  const copy = new Set(current);
                                  if (next) copy.add(key);
                                  else copy.delete(key);
                                  return copy;
                                });
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'Update failed');
                              }
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </OracleTable>
      </SectionCard>
    </Box>
  );
}

function abbreviateStyleName(name: string): string {
  const stripped = name.replace(/^The\s+/i, '');
  return stripped.length > 12 ? `${stripped.slice(0, 11)}…` : stripped;
}

function groupHardware(
  hardware: DooHardware[],
  types: DooHardwareType[],
  collections: Array<{ collection_id: string; collection_name: string }>,
  finishIdsByProduct: Map<string, string[]>,
  finishName: (finishId: string) => string
) {
  const typeName = (id: string | null) =>
    types.find((row) => row.hardware_type_id === id)?.hardware_type_name || 'Ungrouped';
  const collectionName = (id: string | null) =>
    collections.find((row) => row.collection_id === id)?.collection_name;

  const groups = new Map<string, Array<{ id: string; label: string }>>();
  hardware.forEach((product) => {
    const title = typeName(product.hardware_type_id);
    const collection = collectionName(product.collection_id);
    const finishIds = finishIdsByProduct.get(product.hardware_id) ?? [];
    const finishLabel =
      finishIds.length > 0
        ? finishIds.map(finishName).join(', ')
        : 'no finishes configured';
    const baseName = collection ? `${product.hardware_name} (${collection})` : product.hardware_name;
    const label = `${baseName} — ${finishLabel}`;
    const current = groups.get(title) ?? [];
    current.push({ id: product.hardware_id, label });
    groups.set(title, current);
  });
  return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
}
